<script>
  // @ts-nocheck
  import { Plan, NO_CUSTOMER_PORTAL_PAYMENT_TYPES } from '$lib/constants/subscriptions';
  import { authStore } from '$lib/stores/authStore';
  import { formatToHumanDate, getFirstDayOfNextMonth } from '$lib/utils/date';
  import { EventName, trackEvent } from '$lib/services/analytics';
  import { maskStringExceptFirstAndLastTwoChars } from '$lib/utils/common';
  import ApiKey from './ApiKey.svelte';
  import Badge from './Badge.svelte';
  import Progress from './Progress.svelte';

  let isCopied = false;

  function copyApiKey() {
    isCopied = true;
    navigator.clipboard.writeText($authStore.currentUser.api_key);

    alert('API Key copied to clipboard');

    setTimeout(() => {
      isCopied = false;
    }, 100);

    trackEvent(EventName.API_KEY_COPIED);
  }

  async function refreshApiKey() {
    const isConfirmed = window.confirm(
      'Are you certain you want to refresh your API Key? Please note, this action will invalidate your current API Key and all associated access will be revoked. Proceed with caution.'
    );

    if (isConfirmed) {
      await fetch('/api/keys', { credentials: 'include', method: 'POST' });

      trackEvent(EventName.API_KEY_REGENERATED);

      window.location.reload();
    }
  }
</script>

<div class="flex flex-col">
  <!-- User Info Section -->
  <div class="pb-3">
    <div class="flex items-start justify-between mb-5">
      <div class="flex items-center gap-3">
        <img
          src={$authStore.currentUser.user_metadata?.picture}
          alt=""
          class="w-10 h-10 rounded-full"
        />
        <div>
          <h3 class="font-medium text-gray-900">
            {$authStore.currentUser.user_metadata.full_name}
          </h3>
          <p class="text-xs text-gray-500">
            {$authStore.currentUser.user_metadata.email}
          </p>
        </div>
      </div>
      <Badge text={$authStore.currentUser.subscription_plan} />
    </div>

    <!-- Usage Stats -->
    <div class="grid grid-cols-2 gap-3">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-1.5 text-indigo-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            class="w-4 h-4 stroke-current"
            stroke-width="1.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
            />
          </svg>
          <span class="text-xs font-medium">Credits</span>
        </div>
        <div class="flex flex-col gap-1">
          <div class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-gray-900"
              >{$authStore.currentUser.prepaid_credits}</span
            >
            <span class="text-xs text-gray-500">remaining</span>
          </div>
          <a
            href="/other-payment"
            data-sveltekit-reload
            class="text-xs font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-3.5 h-3.5"
            >
              <path
                d="M10.75 6.75a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
              />
            </svg>
            Get more credits
          </a>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-1.5 text-indigo-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            class="w-4 h-4 stroke-current"
            stroke-width="1.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
          <span class="text-xs font-medium">Usage</span>
        </div>
        <div class="space-y-1.5">
          <div class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-gray-900"
              >{$authStore.currentUser.generate_results_count}</span
            >
            <span class="text-xs text-gray-500"
              >/ {$authStore.currentUser.generate_results_limit}</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- API Key Section -->
  <div class="py-1">
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-1.5">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          class="w-4 h-4 stroke-current text-indigo-600"
          stroke-width="1.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
          />
        </svg>
        <span class="text-xs font-medium text-gray-900">API Key</span>
      </div>
      <div class="flex items-center">
        <button
          type="button"
          on:click={copyApiKey}
          class="group relative ml-2 h-9 w-9 items-center justify-center"
        >
          {#if isCopied}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              class="w-4 h-4 stroke-current text-indigo-600 transition group-hover:rotate-[-4deg]"
              stroke-width="1.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75"
              />
            </svg>
          {:else}
            <svg
              class="h-5 w-5 stroke-slate-400 transition group-hover:rotate-[-4deg] group-hover:stroke-slate-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z"
              />
            </svg>
          {/if}
        </button>
        <button
          type="button"
          on:click={refreshApiKey}
          class="group relative ml-2 h-9 w-9 items-center justify-center"
        >
          <svg
            class="h-5 w-5 stroke-slate-400 transition group-hover:rotate-[-4deg] group-hover:stroke-slate-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        </button>
      </div>
    </div>
    <div
      class="font-mono text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 break-all overflow-hidden"
    >
      {maskStringExceptFirstAndLastTwoChars(
        $authStore.currentUser.api_key,
        $authStore.currentUser.api_key.length
      )}
    </div>
  </div>

  <!-- Footer Section -->
  <div class="pt-3 flex flex-col gap-2">
    <p class="text-xs text-gray-500">
      Next reset on <span class="text-indigo-600 font-medium"
        >{formatToHumanDate(getFirstDayOfNextMonth())}</span
      >
    </p>

    {#if !NO_CUSTOMER_PORTAL_PAYMENT_TYPES.includes($authStore.currentUser.payment_type)}
      {#if $authStore.currentUser.subscription_plan !== Plan.FREE}
        <form method="POST" action="/customerPortal">
          <button
            type="submit"
            class="w-full flex items-center justify-center gap-1.5 px-3 py-3 mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-500 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-4 h-4"
            >
              <path
                fill-rule="evenodd"
                d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clip-rule="evenodd"
              />
            </svg>
            Manage subscription
          </button>
        </form>
      {/if}
    {/if}

    {#if $authStore.currentUser.subscription_plan === Plan.FREE}
      <a
        data-sveltekit-reload
        href="/other-payment"
        class="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-500 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="w-4 h-4"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
            clip-rule="evenodd"
          />
        </svg>
        Increase your limit
      </a>
    {/if}
  </div>
</div>
